import { useOrganizationMultisig } from "@/apiQueries/multisigEnrollment/useOrganizationMultisig";
import { useMultisigUnenrollment } from "@/apiQueries/multisigUnenrollment/useMultisigUnenrollment";
import { useUsers } from "@/apiQueries/useUsers";

import { deriveUnenrollmentView, isUnenrollmentEligible } from "@/helpers/unenrollmentView";
import type { UnenrollmentView } from "@/helpers/unenrollmentView";

import type { MultisigUnenrollmentStatus, UserRole } from "@/types";

export type UseUnenrollmentStatusParams = {
  userId: string;
  role: UserRole | null;
  /**
   * The last status a signature or cancel mutation returned. Finished
   * removals vanish from the GET (404), so this keeps the terminal screen
   * reachable after that.
   */
  lastKnownTx?: MultisigUnenrollmentStatus | null;
};

export const useUnenrollmentStatus = ({
  userId,
  role,
  lastKnownTx,
}: UseUnenrollmentStatusParams) => {
  const isOwner = isUnenrollmentEligible(role);

  const orgQuery = useOrganizationMultisig();
  const unenrollmentQuery = useMultisigUnenrollment({ enabled: isOwner });
  const usersQuery = useUsers({ enabled: isOwner });

  const isReady =
    orgQuery.isSuccess && (!isOwner || (unenrollmentQuery.isSuccess && usersQuery.isSuccess));

  const data: UnenrollmentView | undefined = isReady
    ? deriveUnenrollmentView({
        org: orgQuery.data,
        users: usersQuery.data,
        tx: unenrollmentQuery.data,
        lastKnownTx,
        userId,
        role,
      })
    : undefined;

  return {
    data,
    isLoading:
      orgQuery.isLoading || (isOwner && (unenrollmentQuery.isLoading || usersQuery.isLoading)),
    isFetching: orgQuery.isFetching || unenrollmentQuery.isFetching || usersQuery.isFetching,
    isError: orgQuery.isError || unenrollmentQuery.isError || usersQuery.isError,
    error: orgQuery.error ?? unenrollmentQuery.error ?? usersQuery.error ?? null,
    // `refetch()` runs a query even while it is disabled, so a non-Owner must
    // not be sent to the Owner-only endpoints (they would 403 into an error).
    refetch: () =>
      isOwner
        ? Promise.all([orgQuery.refetch(), unenrollmentQuery.refetch(), usersQuery.refetch()])
        : Promise.all([orgQuery.refetch()]),
  };
};
